import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n';
import type { LayoutScopeKey } from '../match-layout';
import { PlaceholderKey } from '../placeholder/placeholder-keys';
import { TemplateType } from './template-type';

/**
 * Per-type metadata for media-manager templates. Open shape: the optional
 * `subtypes` block is present only on types that have a sub-dimension. Other
 * types may grow their own subtype block (possibly with different option lists
 * or `multi: true`) without forcing every entry to declare an empty one.
 */
export interface TemplateTypeSubtypeConfig {
  /** Label shown next to the subtype selector in the UI. */
  label: MessageDescriptor;
  /** Option list rendered in the selector. */
  options: readonly { value: string; label: MessageDescriptor }[];
  /** When true, the UI must allow multiple subtypes to be picked at once. */
  multi: boolean;
  /** When true, the user must pick at least one subtype before submit. */
  required: boolean;
}

export interface TemplateTypeEntry {
  /** Display name in the create/edit modals and the editor header; translate with `t(label)`. */
  label: MessageDescriptor;
  /** Placeholder keys that may appear on a template of this type. */
  allowedPlaceholderKeys: ReadonlySet<PlaceholderKey>;
  /**
   * Which `data.*` field addresses a repeated slot on this type. Drives the
   * «Раскладка …» section and the per-slot filter section in the editor; the
   * render pipeline derives the same key from `ResolverContext.mode`
   * (`run-render-pipeline.ts` → `scopeOf`) and the two must agree — a template
   * authored against `teamNumber` is filtered by `teamNumber` at export.
   *
   * Absent = the type renders exactly one of everything (SCHEDULE, RESULTS), so
   * there is nothing to lay out or filter per slot.
   */
  layoutScopeKey?: LayoutScopeKey;
  /** Present only when the type carries a sub-dimension. */
  subtypes?: TemplateTypeSubtypeConfig;
}

// Why: a placeholder catalog this small reads better as one literal than as
// many tiny constants. The `set(...)` helper keeps each entry to one visual line.
const set = (...keys: PlaceholderKey[]): ReadonlySet<PlaceholderKey> => new Set(keys);

// Table statistics — exclusive to TOURNAMENT_TABLE. Match templates
// (SCHEDULE/RESULTS/MULTIPLE_*) render a single match, not a standings row,
// so these keys never resolve to anything meaningful there and are kept out
// of SCHEDULE_BASE_KEYS below.
const STANDING_KEYS: readonly PlaceholderKey[] = [
  PlaceholderKey.TOURNAMENT_POSITION,
  PlaceholderKey.GAMES_PLAYED,
  PlaceholderKey.GAMES_WON,
  PlaceholderKey.GAMES_LOST,
  PlaceholderKey.GAMES_DRAWN,
  PlaceholderKey.GOALS_SCORED,
  PlaceholderKey.GOALS_CONCEDED,
  PlaceholderKey.GOAL_DIFFERENCE,
  PlaceholderKey.GAMES_DIFFERENCE,
  PlaceholderKey.POINTS,
];

const SCHEDULE_BASE_KEYS: readonly PlaceholderKey[] = [
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
  PlaceholderKey.MATCH_DATE,
  PlaceholderKey.MATCH_TIME,
  PlaceholderKey.MATCH_VENUE,
  PlaceholderKey.ROUND_NAME,
];

const SCHEDULE_KEYS = set(...SCHEDULE_BASE_KEYS);
const MULTIPLE_RESULTS_KEYS = set(...SCHEDULE_BASE_KEYS, PlaceholderKey.SCORE, PlaceholderKey.SCORE_TEAM);
// GOAL_SCORERS is single-match only: a multi-match goal list is out of scope,
// so the two result types no longer share one key set.
const RESULTS_KEYS = set(...SCHEDULE_BASE_KEYS, PlaceholderKey.SCORE, PlaceholderKey.SCORE_TEAM, PlaceholderKey.GOAL_SCORERS);

// GROUP_NAME sits outside STANDING_KEYS: it is stage/group-level metadata, not
// a per-row statistic, and it resolves from the render target rather than from
// a table row.
const TOURNAMENT_TABLE_KEYS = set(
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
  PlaceholderKey.GROUP_NAME,
  ...STANDING_KEYS,
);

// Per-championship player statistics — exclusive to TOP_PLAYERS_BY_INDICATORS,
// the mirror image of STANDING_KEYS.
const PLAYER_STAT_KEYS: readonly PlaceholderKey[] = [
  PlaceholderKey.PLAYER_POSITION,
  PlaceholderKey.PLAYER_GAMES_PLAYED,
  PlaceholderKey.PLAYER_GOALS,
  PlaceholderKey.PLAYER_ASSISTS,
  PlaceholderKey.PLAYER_GOALS_ASSISTS,
  PlaceholderKey.PLAYER_YELLOW_CARDS,
  PlaceholderKey.PLAYER_RED_CARDS,
];

// No ROUND_NAME / MATCH_* / SCORE: the render is scoped to a championship, not a
// match. No GROUP_NAME / STANDING_KEYS: there is no standings table. The team
// keys resolve relative to the player in the slot (addressed by `playerNumber`),
// so this type has no team axis of its own — see `teamMissing` in
// placeholder-key-registry.ts.
const PLAYER_STATS_KEYS = set(
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.PLAYER_NAME,
  PlaceholderKey.PLAYER_NUMBER,
  PlaceholderKey.PLAYER_PHOTO,
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
  ...PLAYER_STAT_KEYS,
);

const COMPETITION_KEYS: readonly PlaceholderKey[] = [
  PlaceholderKey.CHAMPIONSHIP_NAME,
  PlaceholderKey.TOURNAMENT_NAME,
  PlaceholderKey.TOURNAMENT_LOGO,
];

// A single-elimination stage: every pair is addressed by round (from the final)
// and pair, the team keys also by side. No GROUP_NAME / STANDING_KEYS — such a
// stage has no groups and no meaningful points table.
const PLAYOFF_BRACKET_KEYS = set(
  ...SCHEDULE_BASE_KEYS,
  PlaceholderKey.SCORE,
  PlaceholderKey.SCORE_TEAM,
  PlaceholderKey.SCORE_PENALTY_TEAM,
  PlaceholderKey.TEAM_SLOT_PLACEHOLDER,
  PlaceholderKey.STAGE_NAME,
);

// One player and (optionally) their team; nothing repeats, so none of the keys
// is addressed — see `isBirthday` in placeholder-key-registry.ts.
const BIRTHDAY_KEYS = set(
  PlaceholderKey.PLAYER_NAME,
  PlaceholderKey.PLAYER_PHOTO,
  PlaceholderKey.PLAYER_NUMBER,
  PlaceholderKey.PLAYER_POSITION,
  PlaceholderKey.PLAYER_BIRTH_DATE,
  PlaceholderKey.PLAYER_AGE,
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  ...COMPETITION_KEYS,
);

// A slot is a place in the formation (`playerNumber` = rank); the team keys read
// the team of the player standing there, like on the statistics template.
const SYMBOLIC_TEAM_BASE_KEYS: readonly PlaceholderKey[] = [
  PlaceholderKey.PLAYER_NAME,
  PlaceholderKey.PLAYER_PHOTO,
  PlaceholderKey.PLAYER_NUMBER,
  PlaceholderKey.PLAYER_POSITION,
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.SYMBOLIC_TEAM_NAME,
  PlaceholderKey.SYMBOLIC_TEAM_FORMATION,
  PlaceholderKey.SYMBOLIC_SLOT_POSITION,
  ...COMPETITION_KEYS,
];

// ROUND_NAME is the round the team was picked for; it stays empty until the
// backend stores a round on the symbolic team, and the renderer drops it then.
const SYMBOLIC_TEAM_ROUND_KEYS = set(...SYMBOLIC_TEAM_BASE_KEYS, PlaceholderKey.ROUND_NAME);

// Championship-wide statistics of each player, the same source as the
// player-statistics template. There is no per-round statistic on the backend,
// hence none on the round team.
const SYMBOLIC_TEAM_CHAMPIONSHIP_KEYS = set(...SYMBOLIC_TEAM_BASE_KEYS, ...PLAYER_STAT_KEYS);

// Match video review (IMGM-1606). The intro is one match after it was played — the score is known, the goal list is
// not drawn in the browser (GOAL_SCORERS comes from the render service), so it stays off.
const VIDEO_INTRO_KEYS = set(...SCHEDULE_BASE_KEYS, PlaceholderKey.SCORE, PlaceholderKey.SCORE_TEAM);

// The goal plate is about one goal, like the birthday card is about one player: the team keys read the team the goal
// counts for, the player keys its scorer — nothing to address. SCORE is the score right after this goal.
const VIDEO_GOAL_PLATE_KEYS = set(
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.PLAYER_NAME,
  PlaceholderKey.PLAYER_NUMBER,
  PlaceholderKey.PLAYER_PHOTO,
  PlaceholderKey.GOAL_MINUTE,
  PlaceholderKey.SCORE,
  PlaceholderKey.SCORE_TEAM,
  PlaceholderKey.ROUND_NAME,
  ...COMPETITION_KEYS,
);

// A dangerous moment has no team, minute or score of its own — only the match: both teams by home/away.
const VIDEO_DANGER_PLATE_KEYS = set(...SCHEDULE_BASE_KEYS);

export const TEMPLATE_TYPE_REGISTRY: Record<TemplateType, TemplateTypeEntry> = {
  [TemplateType.SCHEDULE]: { label: msg`Расписание`, allowedPlaceholderKeys: SCHEDULE_KEYS },
  [TemplateType.MULTIPLE_SCHEDULE]: {
    label: msg`Несколько матчей (расписание)`,
    allowedPlaceholderKeys: SCHEDULE_KEYS,
    layoutScopeKey: 'matchNumber',
  },
  [TemplateType.RESULTS]: { label: msg`Результаты`, allowedPlaceholderKeys: RESULTS_KEYS },
  [TemplateType.MULTIPLE_RESULTS]: {
    label: msg`Несколько матчей (результаты)`,
    allowedPlaceholderKeys: MULTIPLE_RESULTS_KEYS,
    layoutScopeKey: 'matchNumber',
  },
  [TemplateType.TOURNAMENT_TABLE]: {
    label: msg`Турнирная таблица`,
    allowedPlaceholderKeys: TOURNAMENT_TABLE_KEYS,
    layoutScopeKey: 'teamNumber',
  },
  // Which statistic the ranking runs on used to be a create-time `subtypes`
  // dimension («Показатель»). It is now `template.playerRanking`, edited in the
  // editor: a template may show several stats at once (a goals column *and* an
  // assists column), so pinning the whole template to one indicator was the
  // wrong axis. The DB value stays `top_players_by_indicators` — renaming it
  // would orphan existing rows for no gain.
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: {
    label: msg`Статистика игроков`,
    allowedPlaceholderKeys: PLAYER_STATS_KEYS,
    layoutScopeKey: 'playerNumber',
  },
  [TemplateType.PLAYOFF_BRACKET]: {
    label: msg`Сетка плей-офф`,
    allowedPlaceholderKeys: PLAYOFF_BRACKET_KEYS,
    layoutScopeKey: 'bracketRound',
  },
  [TemplateType.BIRTHDAY]: { label: msg`День рождения`, allowedPlaceholderKeys: BIRTHDAY_KEYS },
  [TemplateType.SYMBOLIC_TEAM_ROUND]: {
    label: msg`Сборная тура`,
    allowedPlaceholderKeys: SYMBOLIC_TEAM_ROUND_KEYS,
    layoutScopeKey: 'playerNumber',
  },
  [TemplateType.SYMBOLIC_TEAM_CHAMPIONSHIP]: {
    label: msg`Сборная турнира`,
    allowedPlaceholderKeys: SYMBOLIC_TEAM_CHAMPIONSHIP_KEYS,
    layoutScopeKey: 'playerNumber',
  },
  [TemplateType.VIDEO_INTRO]: { label: msg`Заставка видео`, allowedPlaceholderKeys: VIDEO_INTRO_KEYS },
  [TemplateType.VIDEO_GOAL_PLATE]: { label: msg`Плашка: гол`, allowedPlaceholderKeys: VIDEO_GOAL_PLATE_KEYS },
  [TemplateType.VIDEO_DANGER_PLATE]: { label: msg`Плашка: опасный момент`, allowedPlaceholderKeys: VIDEO_DANGER_PLATE_KEYS },
};

export function getTemplateTypeEntry(type: TemplateType): TemplateTypeEntry {
  return TEMPLATE_TYPE_REGISTRY[type];
}

export function layoutScopeKeyFor(type: TemplateType | undefined): LayoutScopeKey | undefined {
  return type ? TEMPLATE_TYPE_REGISTRY[type].layoutScopeKey : undefined;
}

export function isPlaceholderKeyValidFor(type: TemplateType, key: string | undefined | null): boolean {
  if (!key) return false;
  return (TEMPLATE_TYPE_REGISTRY[type].allowedPlaceholderKeys as ReadonlySet<string>).has(key);
}

export function availableTemplateTypes(): readonly { value: TemplateType; label: MessageDescriptor }[] {
  return (Object.keys(TEMPLATE_TYPE_REGISTRY) as TemplateType[]).map((t) => ({
    value: t,
    label: TEMPLATE_TYPE_REGISTRY[t].label,
  }));
}
